import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Check, CreditCard, BadgeCheck, ShieldCheck, Sparkles, Landmark } from "lucide-react";

const FEATURES = {
  basic: ["Perfil en el directorio", "Hasta 25 clientes", "Facturación con logo", "Soporte por email"],
  pro: ["Clientes ilimitados", "Gestión de proyectos", "Empleados y control de tiempo", "Posición destacada"],
  enterprise: ["Todo lo de Pro", "Multi-sucursal", "Reportes avanzados", "Gerente de cuenta dedicado"],
};

export default function Subscription() {
  const { t, lang } = useLang();
  const [plans, setPlans] = useState({});
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState("");
  const [code, setCode] = useState("");
  const [codeOk, setCodeOk] = useState(null);
  const [promo, setPromo] = useState(null);
  const [connect, setConnect] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const load = () => api.get("/businesses/mine").then((r) => setBiz(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/discounts/active").then((r) => setPromo(r.data && r.data.active ? r.data : null)).catch(() => {});
    api.get("/connect/status").then((r) => setConnect(r.data)).catch(() => {});
    load();
  }, []);

  const applyCode = async () => {
    if (!code.trim()) return;
    try { const { data } = await api.get(`/discounts/validate?code=${encodeURIComponent(code)}`); setCodeOk(data); toast.success(`${data.code}: ${data.percent}% ${lang === "es" ? "de descuento" : "off"}`); }
    catch { setCodeOk(null); toast.error(lang === "es" ? "Código inválido o expirado" : "Invalid or expired code"); }
  };

  const startTrial = async () => {
    setLoading("trial");
    try { const { data } = await api.post("/subscription/start-trial"); toast.success(`${lang === "es" ? "Prueba gratis activada" : "Free trial started"} · ${data.trial_days} ${lang === "es" ? "días" : "days"}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(""); }
  };

  const subscribe = async (plan) => {
    setLoading(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin, discount_code: codeOk ? codeOk.code : "" });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setLoading(""); }
  };

  const startConnect = async () => {
    setConnecting(true);
    try { const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setConnecting(false); }
  };

  const current = biz?.subscription;
  const order = ["basic", "pro", "enterprise"];
  const commissionNote = order.filter((k) => plans[k]).map((k) => `${lang === "es" ? plans[k].es : plans[k].en} ${plans[k].commission}%`).join(" · ");

  return (
    <div className="pb-10">
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-secondary">{t("dash_subscription")}</h1>
      <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-sm text-slate-500">
        <ShieldCheck className="h-4 w-4 text-accent" /> Pagos seguros con Stripe.
        {commissionNote && <span data-testid="sub-commission-note">Comisión por factura según tu plan: <b className="text-primary">{commissionNote}</b>.</span>}
      </p>

      {/* Stripe Connect */}
      <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between" data-testid="connect-card">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-white"><Landmark className="h-5 w-5" /></span>
          <div>
            <h2 className="font-head text-lg font-bold text-secondary">Cobros a tus clientes (Stripe Connect)</h2>
            <p className="mt-0.5 max-w-xl text-sm text-slate-500">Conecta tu cuenta para recibir los pagos de tus facturas directo en tu banco. La comisión se descuenta automáticamente en cada cobro.</p>
          </div>
        </div>
        {connect?.charges_enabled ? (
          <p className="inline-flex shrink-0 items-center gap-2 rounded-full bg-accent/10 px-4 py-2 text-sm font-bold text-accent" data-testid="connect-active"><Check className="h-4 w-4" /> Conectada</p>
        ) : (
          <Button onClick={startConnect} disabled={connecting} className="shrink-0 rounded-full bg-primary px-5 hover:bg-orange-600" data-testid="connect-btn">
            {connecting ? "…" : (connect?.connected ? "Continuar verificación" : "Conectar cobros")}
          </Button>
        )}
      </div>

      {current && (
        <div className="mt-4 flex items-center gap-3 rounded-2xl border border-accent/40 bg-accent/10 p-4" data-testid="current-subscription">
          <BadgeCheck className="h-5 w-5 text-accent" />
          <span className="text-sm font-semibold text-emerald-800">Plan activo: {lang === "es" ? plans[current.plan]?.es : plans[current.plan]?.en} · ${current.price}/mes · Comisión {plans[current.plan]?.commission ?? current.commission_rate}%</span>
        </div>
      )}

      {!current && (
        <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="trial-banner">
          <span className="text-sm font-semibold text-secondary">{lang === "es" ? "¿Aún no te decides? Prueba gratis todas las funciones." : "Not sure yet? Try all features free."}</span>
          <Button onClick={startTrial} disabled={loading === "trial"} className="rounded-full bg-primary px-5 hover:bg-orange-600" data-testid="start-trial-btn">{loading === "trial" ? "…" : (lang === "es" ? "Iniciar prueba gratis" : "Start free trial")}</Button>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="discount-box">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={lang === "es" ? "Código promocional" : "Promo code"} className="h-10 rounded-full border border-input px-4 text-sm" data-testid="discount-input" />
        <Button variant="outline" className="rounded-full" onClick={applyCode} data-testid="discount-apply">{lang === "es" ? "Aplicar" : "Apply"}</Button>
        {codeOk && <span className="text-sm font-bold text-accent">{codeOk.code}: -{codeOk.percent}%</span>}
      </div>

      {promo && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent" data-testid="sub-promo-banner">
          <Sparkles className="h-4 w-4" />
          {lang === "es" ? `Promoción ${promo.code}: ${promo.percent}% de descuento aplicado en el pago` : `Promo ${promo.code}: ${promo.percent}% off at checkout`}
          {promo.end_date ? (lang === "es" ? ` · hasta ${promo.end_date}` : ` · until ${promo.end_date}`) : ""}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
        {order.map((key) => {
          const p = plans[key];
          if (!p) return null;
          const active = current?.plan === key;
          const highlight = key === "pro";
          return (
            <div key={key} className={`relative flex flex-col rounded-2xl border bg-white p-7 shadow-sm transition-shadow hover:shadow-md ${active ? "border-accent ring-2 ring-accent" : highlight ? "border-primary ring-2 ring-primary" : "border-slate-200"}`} data-testid={`sub-plan-${key}`}>
              {highlight && !active && <span className="absolute -top-3 left-7 rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">Popular</span>}
              {active && <span className="absolute -top-3 left-7 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">Tu plan</span>}
              <h3 className="font-head text-xl font-bold text-secondary">{lang === "es" ? p.es : p.en}</h3>
              {promo ? (
                <div className="mt-3 flex flex-wrap items-baseline gap-2" data-testid={`sub-price-${key}`}>
                  <span className="font-head text-4xl font-extrabold text-secondary">${(p.price * (1 - promo.percent / 100)).toFixed(2)}</span>
                  <span className="text-base font-semibold text-slate-400 line-through">${p.price}</span>
                  <span className="text-slate-500">/mes</span>
                </div>
              ) : (
                <div className="mt-3" data-testid={`sub-price-${key}`}><span className="font-head text-4xl font-extrabold text-secondary">${p.price}</span><span className="text-slate-500">/mes</span></div>
              )}
              <span className="mt-3 inline-flex w-fit items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary" data-testid={`sub-commission-${key}`}>Comisión {p.commission}% por factura</span>
              <ul className="mt-5 flex-1 space-y-2.5">
                {FEATURES[key].map((f) => <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> {f}</li>)}
              </ul>
              <Button onClick={() => subscribe(key)} disabled={loading === key || active} className={`mt-6 h-11 rounded-full text-base disabled:opacity-100 ${active ? "bg-accent text-white hover:bg-accent" : highlight ? "bg-primary hover:bg-orange-600" : "bg-secondary hover:bg-slate-700"}`} data-testid={`sub-select-${key}`}>
                {active ? "Plan actual" : loading === key ? "…" : <><CreditCard className="mr-2 h-4 w-4" />Suscribirme</>}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
